import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Check, Copy, Linkedin, Mail, MessageCircle, Share2 } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { trackShare } from "@/lib/shareAnalytics";
import type { SharePlatform } from "@/lib/shareAnalytics";

export interface ShareMenuProps {
  /** Absolute URL to share. */
  url: string;
  /** Title used by native share sheet and inside emails. */
  title?: string;
  /** Body text used in tweets, WhatsApp messages, emails. URL is appended automatically. */
  text?: string;
  /** Visual style of the trigger. */
  variant?: "default" | "outline" | "secondary" | "ghost" | "link";
  size?: "sm" | "default" | "lg" | "icon";
  /** Trigger label. Defaults to "Share". */
  label?: string;
  className?: string;
  /** Hide label, icon-only trigger. */
  iconOnly?: boolean;
  /** Analytics surface label, e.g. "roast_public", "invite_dialog". */
  surface?: string;
}

/**
 * One-stop share menu used everywhere shareable content lives.
 * Tries the native share sheet on mobile and falls back to a per-network menu.
 */
export function ShareMenu({
  url,
  title = "Glowie",
  text,
  variant = "outline",
  size = "default",
  label = "Share",
  className,
  iconOnly = false,
  surface,
}: ShareMenuProps) {
  const [copied, setCopied] = useState(false);

  const message = text ? `${text} ${url}` : url;
  const enc = encodeURIComponent;

  const tryNativeShare = async () => {
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({ title, text, url });
        trackShare({ platform: "native", surface, url });
        return true;
      } catch (e) {
        if ((e as DOMException)?.name === "AbortError") return true;
      }
    }
    return false;
  };

  const handleTriggerClick = async (e: React.MouseEvent) => {
    // On mobile, prefer native share sheet. On desktop, let the menu open.
    const isMobile =
      typeof window !== "undefined" && window.matchMedia?.("(max-width: 640px)").matches;
    if (isMobile && (await tryNativeShare())) {
      e.preventDefault();
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied");
      trackShare({ platform: "copy", surface, url });
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Couldn't copy. Long-press the link to copy.");
    }
  };

  const openWindow = (href: string, platform: SharePlatform) => {
    trackShare({ platform, surface, url });
    window.open(href, "_blank", "noopener,noreferrer,width=600,height=600");
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild onClick={handleTriggerClick}>
        <Button variant={variant} size={size} className={cn("rounded-full", className)}>
          <Share2 className={cn("h-4 w-4", !iconOnly && "mr-2")} />
          {!iconOnly && label}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>Share with friends</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={copyLink}>
          {copied ? <Check className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
          {copied ? "Copied" : "Copy link"}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => openWindow(`https://wa.me/?text=${enc(message)}`, "whatsapp")}>
          <MessageCircle className="h-4 w-4 mr-2" /> WhatsApp
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() =>
            openWindow(
              `https://twitter.com/intent/tweet?text=${enc(text ?? title)}&url=${enc(url)}`,
              "x",
            )
          }
        >
          <span className="font-bold w-4 mr-2 text-center">𝕏</span> X / Twitter
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => openWindow(`https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`, "facebook")}
        >
          <span className="font-bold w-4 mr-2 text-center">f</span> Facebook
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() =>
            openWindow(`https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}`, "linkedin")
          }
        >
          <Linkedin className="h-4 w-4 mr-2" /> LinkedIn
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => {
            trackShare({ platform: "email", surface, url });
            window.location.href = `mailto:?subject=${enc(title)}&body=${enc(message)}`;
          }}
        >
          <Mail className="h-4 w-4 mr-2" /> Email
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default ShareMenu;